// Config → Trade Account Discounts (Task Module 07).
// The single source of truth for VIP tier pricing. Each tier is a discount off
// the LIVE retail website price (ex VAT) — applied everywhere (Quote Builder +
// Data Lab product tier view). Admin edits; changes affect NEW pricing actions
// only (existing saved quotes are never rewritten).
import { useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { toast } from "sonner";
import { BadgePercent, Save, Loader2, Info } from "lucide-react";

const GBP = (n) =>
  n === null || n === undefined || !Number.isFinite(Number(n))
    ? "—"
    : Number(n).toLocaleString("en-GB", { style: "currency", currency: "GBP" });

// Worked-example figures so admins see the effect of a % change immediately.
const SAMPLE = { base: 27.96, cost: 22.96, label: "Rockwool RWA45 · base £27.96 ex VAT / cost £22.96" };

function samplePrice(base, disc) {
  return Math.round(base * (1 - (Number(disc) || 0) / 100) * 100) / 100;
}
function sampleMargin(price, cost) {
  return price > 0 ? ((price - cost) / price) * 100 : 0;
}

export default function TradeAccountDiscounts() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/config/pricing-tiers");
      setTiers(data.tiers || []);
      setDirty(false);
    } catch {
      toast.error("Failed to load tier discounts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (tier, patch) => {
    setTiers((prev) => prev.map((t) => (t.tier === tier ? { ...t, ...patch } : t)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        tiers: tiers.map((t) => ({
          tier: t.tier,
          label: t.label,
          discount_pct: t.custom ? null : Number(t.discount_pct) || 0,
          custom: !!t.custom,
          active: t.active !== false,
        })),
      };
      const { data } = await api.put("/config/pricing-tiers", payload);
      setTiers(data.tiers || []);
      setDirty(false);
      toast.success("Trade account discounts saved", {
        description: "Applies to new pricing actions. Existing quotes are unchanged.",
      });
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const updatedInfo = useMemo(() => {
    const t = tiers.find((x) => x.updated_at);
    return t ? `Last updated ${new Date(t.updated_at).toLocaleString("en-GB")} · ${t.updated_by || "—"}` : "";
  }, [tiers]);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-8 px-4" data-testid="trade-discounts-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2.5">
              <BadgePercent className="w-6 h-6 text-orange-400" />
              <h1 className="text-2xl font-bold text-white tracking-tight">Trade Account Discounts</h1>
            </div>
            <p className="text-sm text-slate-400 mt-1.5 max-w-2xl leading-relaxed">
              Each VIP tier is a <b className="text-slate-200">discount off the live retail price</b> (ex VAT),
              matching the website Trade Account pricing. These values are hard-set across the whole
              system — Quote Builder and product views. Margin is derived automatically.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="trade-discounts-save"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              dirty && !saving
                ? "bg-orange-500 hover:bg-orange-400 text-black"
                : "bg-white/5 text-slate-500 cursor-not-allowed"
            }`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        <div className="flex items-start gap-2 text-xs text-slate-400 bg-sky-500/5 border border-sky-500/20 rounded-lg px-3.5 py-2.5 mb-5" data-testid="trade-discounts-note">
          <Info className="w-4 h-4 text-sky-300 shrink-0 mt-0.5" />
          <span>
            Editing a tier changes <b className="text-slate-200">new pricing actions only</b> — line prices on
            already-saved quotes are never rewritten.
          </span>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-20">Loading…</div>
        ) : (
          <div className="rounded-xl border border-white/10 overflow-hidden bg-[#0E1524]">
            <table className="w-full text-sm" data-testid="trade-discounts-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-400 bg-white/[0.03]">
                  <th className="text-left font-mono font-semibold px-4 py-3">Tier</th>
                  <th className="text-left font-mono font-semibold px-4 py-3">Label</th>
                  <th className="text-right font-mono font-semibold px-4 py-3 w-32">Discount %</th>
                  <th className="text-right font-mono font-semibold px-4 py-3 w-28">Sample price</th>
                  <th className="text-right font-mono font-semibold px-4 py-3 w-24">Margin</th>
                  <th className="text-center font-mono font-semibold px-4 py-3 w-20">Active</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((t) => {
                  const price = t.custom ? SAMPLE.base : samplePrice(SAMPLE.base, t.discount_pct);
                  const margin = t.custom ? null : sampleMargin(price, SAMPLE.cost);
                  return (
                    <tr key={t.tier} className="border-t border-white/[0.06]" data-testid={`trade-discount-row-${t.tier}`}>
                      <td className="px-4 py-3 font-bold text-white">{t.tier}</td>
                      <td className="px-4 py-3">
                        <input
                          className="w-full bg-white/5 border border-white/10 rounded px-2.5 py-1.5 text-slate-200 text-[13px] focus:outline-none focus:border-orange-400/60"
                          value={t.label || ""}
                          onChange={(e) => setField(t.tier, { label: e.target.value })}
                          data-testid={`trade-discount-label-${t.tier}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        {t.custom ? (
                          <span className="font-mono text-xs text-violet-300 uppercase tracking-wide">Custom</span>
                        ) : (
                          <div className="inline-flex items-center gap-1">
                            <input
                              type="number" step="0.1" min="0" max="100"
                              className="w-20 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-right font-mono text-slate-100 text-[13px] focus:outline-none focus:border-orange-400/60"
                              value={t.discount_pct ?? 0}
                              onChange={(e) => setField(t.tier, { discount_pct: e.target.value })}
                              data-testid={`trade-discount-pct-${t.tier}`}
                            />
                            <span className="text-slate-500 text-xs">%</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[13px] text-[#C8A46B]" data-testid={`trade-discount-sample-${t.tier}`}>
                        {t.custom ? "—" : GBP(price)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-[13px]" data-testid={`trade-discount-margin-${t.tier}`}>
                        {margin === null ? (
                          <span className="text-slate-500">—</span>
                        ) : (
                          <span className={margin >= 6 ? "text-emerald-400" : margin >= 3 ? "text-amber-400" : "text-red-400"}>
                            {margin.toFixed(1)}%
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={t.active !== false}
                          onChange={(e) => setField(t.tier, { active: e.target.checked })}
                          data-testid={`trade-discount-active-${t.tier}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-4 py-2.5 text-[11px] font-mono text-slate-500 bg-white/[0.02] border-t border-white/[0.06] flex items-center justify-between">
              <span>Sample: {SAMPLE.label}</span>
              {updatedInfo && <span>{updatedInfo}</span>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
